import type { ListBuilderConfiguration } from '../types';
import {
	buildEcommerceContactColumn,
	buildEcommerceDateAndUserColumn,
} from './ecommerce-person-columns';

/**
 * Login History list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const LOGIN_HISTORY_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'login-history-crm-users',
			cacheTTL: 600000,
			name: 'CRM Users',
			type: 'default',
			active: true,
			sortOrder: 0,
			// PD-14200 — users holding only the ecommerce-scoped permission
			// (access_logs:login_history:ecommerce:read) never see CRM logins.
			visibleIf: 'access_logs:login_history:read',
			columns: [
				{
					id: 'date',
					headers: [
						{
							id: 'date-header',
							text: 'Date',
						},
					],
					fields: [
						{
							id: 'date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'date',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
								formatOptions: {
									dateFormat: 'datetime-short',
								},
								allowTextWrap: false,
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'login_history',
							fieldName: 'date',
							direction: 'DESC',
						},
					],
					width: 160,
					minWidth: 160,
					maxWidth: 160,
					verticalAlignment: 'top',
				},
				{
					id: 'user',
					headers: [
						{
							id: 'user-header',
							text: 'User',
						},
						{
							id: 'user-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'user-email-header',
							text: 'Email',
						},
					],
					fields: [
						{
							id: 'user-name-field',
							dataConfig: {
								type: 'template',
								template: '{user.firstName} {user.lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-user',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'user-email-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'user.email',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'device-location',
					headers: [
						{
							id: 'device-header',
							text: 'Device',
						},
						{
							id: 'device-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'ip-header',
							text: 'IP',
						},
					],
					fields: [
						{
							id: 'device-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'parsedUserAgent.summary',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-device',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'ip-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'ip',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
			],
			filterRules: {
				loginHistoryUserType: {
					defaultValue: [
						{
							id: 'crm',
							label: 'CRM',
						},
					],
					// PD-14200 — without the full permission the user type may
					// not be switched (the whole CRM list is hidden anyway via
					// visibleIf; this is defence in depth for saved copies).
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{
								type: 'rule',
								action: 'access_logs:login_history:read',
								negated: true,
							},
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{
								type: 'rule',
								action: 'access_logs:login_history:read',
								negated: true,
							},
						],
					},
				},
				// PD-14200 — client / sales-rep filters only make sense for
				// ecommerce (portal) logins; hide them on the CRM list.
				loginHistoryClient: {
					hidden: true,
				},
				loginHistorySalesRep: {
					hidden: true,
				},
			},
		},
		{
			id: 'login-history-ecommerce-users',
			cacheTTL: 600000,
			name: 'Storefront Users',
			type: 'default',
			active: true,
			sortOrder: 1,
			// PD-14200 — this list gets the client/sales-rep filters on top of
			// the shared search + date range.
			activeFilters: [
				'loginHistorySearch',
				'loginHistoryDateRange',
				'loginHistoryClient',
				'loginHistorySalesRep',
			],
			columns: [
				// PD-17191 — the user's name sits under the date, and a Contact
				// column shows the email over the phone.
				buildEcommerceDateAndUserColumn({
					objectTable: 'login_history',
					nameTemplate: '{user.firstName} {user.lastName}',
				}),
				buildEcommerceContactColumn({ objectTable: 'login_history' }),
				{
					id: 'device-location',
					headers: [
						{
							id: 'device-header',
							text: 'Device',
						},
						{
							id: 'device-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'ip-header',
							text: 'IP',
						},
					],
					fields: [
						{
							id: 'device-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'parsedUserAgent.summary',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-device',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'ip-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'ip',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'customer',
					headers: [
						{
							id: 'customer-header',
							text: 'Customer',
						},
						{
							id: 'customer-break',
							text: '',
							displayStyle: 'line_break',
						},
						{
							id: 'customer-sales-rep-header',
							text: 'Sales Rep',
						},
					],
					fields: [
						{
							id: 'customer-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientNames',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-customer',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'customer-sales-rep-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'salesRepNames',
									objectTable: 'login_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
			],
			filterRules: {
				loginHistoryUserType: {
					defaultValue: [
						{
							id: 'ecommerce',
							label: 'Ecommerce',
						},
					],
					// PD-14200 — ecommerce-only viewers cannot switch to CRM
					// logins. Also enforced server-side by the crm-api proxy,
					// which forces userType=ecommerce for callers without the
					// full permission.
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{
								type: 'rule',
								action: 'access_logs:login_history:read',
								negated: true,
							},
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{
								type: 'rule',
								action: 'access_logs:login_history:read',
								negated: true,
							},
						],
					},
				},
				// PD-14200 — users without clients:detail:any only see logins
				// for their own clients' portal users: rep filter defaults to
				// and is locked on "Me" (server-enforced by the crm-api proxy).
				loginHistorySalesRep: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
				},
			},
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: ['loginHistorySearch', 'loginHistoryDateRange'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/settings/login-history',
			objectLabel: 'Login History',
			objectTable: 'login_history',
			selectedFields: [
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'user.firstName', fieldLabel: 'First Name' },
				{ fieldName: 'user.lastName', fieldLabel: 'Last Name' },
				{ fieldName: 'user.email', fieldLabel: 'Email' },
				// PD-17191 — display-ready contact, filled in by the crm-api proxy.
				{ fieldName: 'userEmail', fieldLabel: 'Contact Email' },
				{ fieldName: 'userPhone', fieldLabel: 'Phone' },
				{ fieldName: 'ip', fieldLabel: 'IP Address' },
				{ fieldName: 'authMethod', fieldLabel: 'Auth Method' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'parsedUserAgent.summary', fieldLabel: 'Device' },
				{ fieldName: 'clientNames', fieldLabel: 'Customer' },
				{ fieldName: 'salesRepNames', fieldLabel: 'Client Sales Rep' },
			],
		},
	],
};
